/**
 * Storage helpers with type safety
 * 
 * Token is stored standardly in localStorage to keep users logged in.
 */

const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  SHOP: 'shop',
} as const;

/**
 * Save JWT token
 */
export const saveToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
};

/**
 * Get JWT token
 */
export const getToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

/**
 * Clear JWT token
 */
export const clearToken = (): void => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
};

/**
 * Save user object to localStorage
 */
export const saveUser = (user: unknown): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  } catch (err) {
    console.error('Failed to save user to localStorage', err);
  }
};

/**
 * Get user object from localStorage
 */
export const getUser = (): unknown | null => {
  const user = localStorage.getItem(STORAGE_KEYS.USER);
  if (!user) return null;
  try {
    return JSON.parse(user);
  } catch {
    console.error('Failed to parse user from localStorage');
    return null;
  }
};

/**
 * Clear user object from localStorage
 */
export const clearUser = (): void => {
  localStorage.removeItem(STORAGE_KEYS.USER);
};

/**
 * Clear all session data (logout)
 */
export const clearSession = (): void => {
  clearToken();
  clearUser();
  localStorage.removeItem(STORAGE_KEYS.SHOP);
};

/**
 * Check if user is authenticated (has token)
 */
export const isAuthenticated = (): boolean => {
  return getToken() !== null;
};
